/**
 * BaskReport 报表中间层（IR）类型定义。
 *
 * 设计原则：
 * - 这是「AI 产出的精简 JSON」的契约，字段尽量贴近语义，避免机械属性爆炸。
 * - IR 采用 camelCase；序列化到 XML 时由 serialize.ts 做**显式映射**（因官方 Schema
 *   键名与 XML 属性名并不一致，例如 IR 的 `expand` 对应 XML `expand`，
 *   但 `leftCell` 对应 XML `left`，且部分字段 XML 直接用 camelCase 如 `buildinDatasource`）。
 * - 所有坐标从 1 开始；单元格用 (row, col) 唯一标识，也允许直接给 name（A1 风格）；
 *   转换器在网格展开阶段统一补全 name。
 */

/** 扩展方向：决定单元格随数据如何延展。none=静态，down=向下扩展，right=向右扩展。 */
export type ExpandMode = 'none' | 'down' | 'right';

/** 聚合类型（对齐官方 AggregateType 全集，与 structureCheck.AGG_TYPES 保持一致）：
 * group=分组（去重），conditiongroup=条件分组，expressiongroup=表达式分组，sum=求和，count=计数，
 * countd=去重计数，avg=平均，max/min=极值，percentile=百分位，variance=方差，std=标准差，
 * select=选取，none=不聚合（明细，序列化归并 select）。 */
export type AggregateType =
  | 'none'
  | 'group'
  | 'conditiongroup'
  | 'expressiongroup'
  | 'select'
  | 'sum'
  | 'avg'
  | 'max'
  | 'min'
  | 'count'
  | 'countd'
  | 'percentile'
  | 'variance'
  | 'std';

/**
 * 比较运算符（对齐官方过滤器比较符全集，见 methodology/filter/filter.md）：
 * - `==` 等于、`>` 大于、`<` 小于、`>=` 大于等于、`<=` 小于等于、`!=` 不等于（表达式语法 OP 规则，ReportLexer.g4）；
 * - `contains` 包含、`notContains` 不包含、`startsWith` 开始于、`notStartsWith` 不开始于、
 *   `endsWith` 结束于、`notEndsWith` 不结束于（过滤器解析 Op.java 的单词形式）；
 * - `in` 在集合中、`notIn` 不在集合中。
 */
export type ComparisonOp =
  | '=='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'notStartsWith'
  | 'endsWith'
  | 'notEndsWith'
  | 'in'
  | 'notIn';

/** 数据集字段类型（样本实测取值）。 */
export type FieldType = 'string' | 'number' | 'date' | 'boolean';

/**
 * 报表查询参数类型（BaskReport 官方参数类型，区别于数据集字段 FieldType）。
 * 序列化映射为 XML `datatype`/`param-type`：String / Number / Boolean / Date / Datetime / List。
 * - `datetime` 是参数独立类型（日期时间），勿与字段 `date` 混淆；
 * - `list` 表示集合/多选参数（如下拉多选）。
 */
export type ParameterType = 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'list';

/** 边框线型。 */
export type BorderStyle = 'solid' | 'dashed' | 'dotted' | 'none';

/**
 * 数据集单元格的关联条件（主子报表用）。
 * 序列化为 dataset-content 下的 <condition>，使子数据按父字段过滤，
 * 例如子数据集 employee 仅展示 DEPT_ID 与父数据集 dept.DEPT_ID 相等的行。
 */
export interface DatasetConditionIR {
  /** 关联字段名（本数据集字段），对应 XML condition 的 left。 */
  left: string;
  /** 比较运算符（官方比较符全集，见 ComparisonOp）。 */
  op: ComparisonOp;
  /**
   * 值类型：
   * - dataset：与另一数据集的某个字段关联（主子报表，valueType=dataset，需给 dataset+field）；
   * - expression：按表达式值（如 ${${$p1}}，需给 value）；
   * - string：常量字符串（需给 value）。
   */
  valueType: 'dataset' | 'expression' | 'string';
  /** valueType=dataset 时，关联的数据集名与字段名。 */
  dataset?: string;
  field?: string;
  /** valueType=expression/string 时的比较值。 */
  value?: string;
}

/** 单元格内容：AI 只需描述语义意图，转换器负责展开成完整网格。 */
export type CellContent =
  | { kind: 'text'; value: string }
  | { kind: 'expression'; value: string } // 如 "=sum(D1)" 或 "=if(C4 && D4){return C4*D4}"
  | {
      kind: 'dataset';
      dataset: string;
      field: string;
      aggregate?: AggregateType;
      /** 表达式分组（aggregate=expressiongroup）的分组表达式：以 # 代表当前字段值，
       *  如 "formatDate(#,\"yyyy\")" 按年分组、"formatDate(#,\"yyyy-MM\")" 按月分组。
       *  仅 aggregate=expressiongroup 时生效，序列化为官方 <group-expression>。
       *  注意：style.format 只改显示、不改分组键，不能替代 groupExpression。 */
      groupExpression?: string;
      /** 主子报表关联条件（如按父数据集字段过滤子数据）。 */
      conditions?: DatasetConditionIR[];
    }
  | {
      kind: 'crosstab';
      /** 斜线方向：down=左上到右下（缺省），right=左下到右上。 */
      direction?: 'down' | 'right';
      /** 角区分区文字：列维度标签在前、行维度标签在后，顺序即 <item> 顺序。
          仅序列化阶段用于生成斜线表头 <item>，IR 不写几何（degree/xPos/yPos 由序列化计算）。 */
      cornerLabels?: { row: string[]; col: string[] };
    };

/** 单格边框配置；未设置的边使用默认值（细黑实线）。 */
export interface CellBorder {
  style?: BorderStyle;
  width?: number;
  color?: string; // 十六进制，如 #000000
}

/** 单元格样式（均为可选，缺省取引擎默认值）。 */
export interface StyleIR {
  align?: 'left' | 'center' | 'right';
  valign?: 'top' | 'middle' | 'bottom';
  forecolor?: string; // 文字颜色 #000000
  bgcolor?: string; // 背景色 #ffffff
  fontFamily?: string; // 如 "微软雅黑"
  /** 字号；缺省由序列化端兜底 DEFAULTS.fontSize（10）。默认不写，仅显式配置才出现。 */
  fontSize?: number;
  /**
   * 加粗/斜体/下划线/删除线：官方 CellDefinitionParser 读取的布尔样式键为
   * `bold` / `italic` / `underline` / `line-through`（注意不是 font-weight / font-style
   * / strikethrough，否则会被静默忽略）。仅在显式 true 时输出对应属性。
   */
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  lineThrough?: boolean;
  /** 文本方向，如 "horizontal" / "vertical"（官方 text-direction）。 */
  textDirection?: string;
  /** 字距，官方 letter-spacing。 */
  letterSpacing?: number;
  /** 是否可见，官方 visible。 */
  visible?: boolean;
  /** 数字/日期格式化，如 "#.##" 或 "yyyy-MM-dd"。 */
  format?: string;
  /** 格式化类型，官方 FormatType 枚举：text/date/currency/number/custom（缺省 number）。 */
  formatType?: 'text' | 'date' | 'currency' | 'number' | 'custom';
  border?: {
    left?: CellBorder;
    right?: CellBorder;
    top?: CellBorder;
    bottom?: CellBorder;
  };
  /** 条件样式（隔行变色、阈值高亮等）。 */
  render?: CellRender[];
}

/** 条件样式：满足 condition 时对 scope 应用 bgcolor/color。 */
export interface CellRender {
  /** 作用范围：cell=当前格，row=整行。 */
  scope?: 'cell' | 'row';
  bgcolor?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** 条件表达式，如 "C4 > 1000"，运算符 > 在 JSON 中直接写。 */
  leftExpression: string;
  op: ComparisonOp;
  rightExpression: string;
}

/** 单元格录入控件（填报）。 */
export interface CellControlIR {
  type: 'text' | 'select' | 'date' | string;
  visible?: boolean;
  enableEmpty?: boolean;
  enableEdit?: boolean;
  enableCustom?: boolean;
  datePattern?: string;
  verify?: {
    type: 'length' | 'decimal' | 'email' | string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
  };
  binding?: {
    type: 'custom' | string;
    customData?: Array<{ label: string; value: string }>;
  };
}

/** 单元格显示值映射（facade）。 */
export interface CellFacadeIR {
  mappingType?: 'custom' | string;
  showOriginalValueOnMappingFail?: boolean;
  customMappingData?: Array<{ label: string; value: string }>;
}

/** 填报字段映射（单元格坐标 → 数据表列）。 */
export interface FillFieldIR {
  name: string;
  type: string; // 数据表列类型，如 string/number
  primaryKey?: boolean;
  autoIncrement?: boolean;
  /** 取值来源，缺省 cell。 */
  valueType?: 'cell' | string;
  /** 取值单元格坐标（A1 风格），如 A2。 */
  value: string;
}

/** 填报提交配置（单表写回）。 */
export interface FillSubmitIR {
  name: string;
  type?: 'sql' | string;
  submitType?: string; // 如 insert/update/delete
  datasource: string; // 关联 datasources[].name
  table: string;
  fields: FillFieldIR[];
}

/** 填报中间层（仅填报类报表有）。 */
export interface FillIR {
  submit: FillSubmitIR;
}

/** 参数查询表单元素（对应 XML <form-element>）。 */
export interface FormElementIR {
  /** 表单元素标识；param 型对应参数名，submit 型对应按钮文案。 */
  name: string;
  formType: 'param' | 'submit' | string;
  paramType?: string; // 仅 param 型有，必须是 IR 小写枚举（string/number/date/boolean）
  label?: string;
  editorType?: string; // 如 select/button/text
  width?: number;
  required?: boolean;
  left?: number; // px 坐标
  top?: number; // px 坐标
  submitOnChange?: boolean;
  applyPrefixSuffixWhenEmpty?: boolean;
  binding?: {
    type: 'custom' | string;
    customData?: Array<{ label: string; value: string }>;
  };
}

/** 单元格中间层描述（稀疏写法）。 */
export interface CellIR {
  row: number; // 从 1 开始
  col: number; // 从 1 开始
  name?: string; // 可选，缺省由 (row,col) 推导为 A1 风格坐标
  content?: CellContent;
  expand?: ExpandMode; // 缺省 none
  /** 左父格坐标（A1 风格）或 "default"；缺省由 parent.ts 推导。 */
  left?: string;
  /** 上父格坐标（A1 风格）或 "default"。 */
  top?: string;
  rowspan?: number; // 缺省 1；>1 表示合并
  colspan?: number; // 缺省 1；>1 表示合并
  visible?: boolean; // 缺省 true
  forcePagingBefore?: boolean;
  forcePaging?: boolean;
  /** 单元格级聚合类型（对应 XML aggregate-type）。 */
  aggregateType?: AggregateType;
  /** 聚合方向：left=向左聚合，top=向上聚合，both=双向。 */
  aggregateOn?: 'left' | 'top' | 'both';
  style?: StyleIR;
  /** 录入控件（填报）。 */
  control?: CellControlIR;
  /** 显示值映射（facade）。 */
  facade?: CellFacadeIR;
}

/** 行定义（未列出的行取默认行高）。 */
export interface RowIR {
  num: number; // 从 1 开始
  height?: number;
  band?: 'headerrepeat' | 'none';
  lock?: boolean;
  /** 是否可见，对应 XML row 的 visible，缺省 true。 */
  visible?: boolean;
  /** 是否自动行高，对应 XML row 的 auto-height。 */
  autoHeight?: boolean;
}

/** 列定义（未列出的列取默认列宽）。 */
export interface ColumnIR {
  num: number; // 从 1 开始
  width?: number;
  lock?: boolean;
  /** 是否可见，对应 XML col 的 visible，缺省 true。 */
  visible?: boolean;
  /** 是否自动列宽，对应 XML col 的 auto-width。 */
  autoWidth?: boolean;
}

/** 数据集字段。 */
export interface DatasetFieldIR {
  name: string;
  label?: string;
  type: FieldType;
}

/** 数据集。type 取值：sql（SQL 数据集）、buildin（内置/内联数据）、api（API 服务端数据集）、
 * bean（服务端已注册的 Java Bean 数据集）。
 * api 为服务端数据集；adapt/virtual 是「数据源」层面类型（datasources[].datasourceType），
 * 不属于数据集类型。序列化时若关联数据源的 datasourceType 为 api/adapt/virtual，
 * 数据集 type 跟随数据源真实类型名输出并附带 dataset-id 定位。
 * bean 无 datasource、无 sql，仅声明名称与字段（字段来自 list-bean-fields 探查结果）。 */
export interface DatasetIR {
  name: string;
  type: 'sql' | 'buildin' | 'api' | 'bean';
  datasource: string; // 关联 datasources[].name（bean 类型留空 ""）
  sql?: string; // type=sql 时必填
  /** 内联数据（buildin 数据集），JSON 字符串。 */
  data?: string;
  fields: DatasetFieldIR[];
  readFieldRemark?: boolean;
}

/** 服务端数据源类型（对应 XML 的 datasource-type，kind=server 时生效）。 */
export type ServerDatasourceType = 'jdbc' | 'virtual' | 'api' | 'adapt';

/** 内置数据源类型（对应 XML 的 datasource-type，kind=buildin 时生效）。 */
export type BuildinDatasourceType = 'custom' | 'jndi';

/** 数据源（顶层元素，link by name）。 */
export interface DatasourceIR {
  name: string;
  type: string; // 如 mysql / oracle
  kind?: 'buildin' | 'server'; // buildin=内置数据源；server=引用服务端已配置数据源（推荐）
  id?: string; // 服务端数据源 ID（kind=server 必填，如 samplecenter-jdbc）
  /** server 分支用：jdbc/virtual/api/adapt（缺省 jdbc）；buildin 分支用：custom/jndi。 */
  datasourceType?: ServerDatasourceType | BuildinDatasourceType;
  validationQuery?: string;
  buildinDatasource?: string; // 如 SampleCenterDatasource（仅 kind=buildin 生效）
}

/** 分栏定义（列分栏报表用）。<page-setting> 内输出 <column cell=".." number=".."/>。 */
export interface ColumnBandIR {
  /** 分栏起始单元格坐标（A1 风格），如 "B2"。 */
  cell: string;
  /** 每栏列数，如 10。 */
  number: number;
}

/** 报表水印（对应 XML <setting> 内的 <watermaker>）。 */
export interface WatermarkIR {
  /** 水印文本，如 "员工明细报表"。 */
  text: string;
  /** 水印左侧坐标 px，缺省 10。 */
  left?: number;
  /** 水印顶部坐标 px，缺省 5。 */
  top?: number;
  /** 旋转角度（度），缺省 -30。 */
  rotate?: number;
  /** 文字颜色，缺省 #dddddd。 */
  forecolor?: string;
  /** 字号，缺省 40。 */
  fontSize?: number;
  /** 字体，缺省 微软雅黑。 */
  fontFamily?: string;
  /** 加粗/斜体/下划线，缺省 false。 */
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

/** 纸张与页边距。 */
export interface PaperIR {
  paper?: 'A4' | 'A5' | 'A6' | 'B5' | 'custom';
  paperWidth?: number; // mm，custom 时必填
  paperHeight?: number; // mm
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  orientation?: 'portrait' | 'landscape';
  /** 分栏定义（列分栏报表），多个分栏依次输出。 */
  columns?: ColumnBandIR[];
}

/** 查询参数（参数报表用）。 */
export interface ParameterIR {
  name: string;
  label?: string;
  /** 参数类型：string/number/boolean/date/datetime/list（区别于字段 FieldType，见 ParameterType）。 */
  type?: ParameterType;
  defaultValue?: string;
  /**
   * 查询表单编辑器类型，对应 BaskReport XML 的 `editor-type`（如 text / select / date）。
   * 缺省为 `text`。
   */
  editorType?: string;
  /** 查询表单控件宽度（px），对应 `width`，缺省 120。 */
  width?: number;
  /** 是否必填，对应 `required`，缺省 false。 */
  required?: boolean;
  /** 查询表单元素左侧坐标 `left`（px）；缺省自动横向排列。 */
  left?: number;
  /** 查询表单元素顶部坐标 `top`（px）；缺省 36。 */
  top?: number;
  /** 值变更时是否自动提交，对应 `submit-on-change`，缺省 false。 */
  submitOnChange?: boolean;
  /** 值为空时是否仍应用前缀/后缀，对应 `apply-prefix-suffix-when-empty`，缺省 false。 */
  applyPrefixSuffixWhenEmpty?: boolean;
  /**
   * 下拉选择等控件的数据绑定（如 editor-type=select 时的可选项）。
   * 对应 BaskReport XML `<form-element>` 内的 `<binding>`。
   */
  binding?: {
    type: 'custom' | string;
    customData?: Array<{ label: string; value: string }>;
  };
}

/**
 * 参数占位符约定（唯一标准写法）。
 *
 * BaskReport 参数在 SQL 中以 `${$name}` 形式引用，其中 **`$` 是占位符语法的一部分，
 * 不是参数名本身**。即：
 *   - parameters 中声明 `{ "name": "dept" }`（参数名不带 `$`）；
 *   - SQL 中写 `... where DEPT_ID = ${$dept} ...`（`$` 紧跟参数名，属于占位符语法）。
 *
 * 历史上存在 `:param` 与 `${param}` 两种误写，现已统一为 `${$name}`：
 * - `:param` 不是本引擎的参数语法；
 * - `${param}` 缺前导 `$`，无法被引擎识别为参数占位符。
 *
 * 下列工具函数供校验/序列化阶段复用，避免各处自行拼占位符字符串。
 */

/** SQL 中参数占位符的前缀（含 `${$`，后缀为 `}`）。 */
export const PARAM_PLACEHOLDER_PREFIX = '${$';

/**
 * 从 SQL 文本中提取所有参数占位符引用的参数名（剥离前导 `$`）。
 * 例：`where a = ${$dept} and b = ${$degree}` → `['dept', 'degree']`。
 * 仅识别 `${$name}` 标准写法。
 */
export function extractParamPlaceholders(sql: string): string[] {
  const out = new Set<string>();
  const re = /\$\{\$([A-Za-z_][A-Za-z0-9_$]*)\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(sql))) out.add(m[1]!);
  return [...out];
}

/**
 * 把 SQL 中的 `${$name}` 占位符替换为中性字面量 `null`，
 * 使含查询条件的 param-query SQL 也能被服务端 buildFields 正常解析（校验只关心表/字段结构）。
 */
export function stripParamPlaceholders(sql: string): string {
  return sql.replace(/\$\{\$[A-Za-z_][A-Za-z0-9_$]*\}/g, 'null');
}

/** 判断一个参数名是否合法（参数名本身不含 `$`，仅由字母/数字/下划线组成）。 */
export function isParamName(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_$]*$/.test(name) && !name.startsWith('$');
}

/**
 * 把参数名渲染为标准 SQL 占位符 `${$name}`。
 * 入参为参数名本身（如 `dept`），统一输出 `${$dept}`。
 */
export function toParamPlaceholder(name: string): string {
  const bare = name.startsWith('$') ? name.slice(1) : name;
  return `${PARAM_PLACEHOLDER_PREFIX}${bare}}`;
}

/** 报表根中间层。 */
export interface ReportIR {
  /** 报表名称（仅用于提示，不影响引擎）。 */
  name?: string;
  /** 网格总行数，转换器据此补全空白单元格。 */
  rowCount: number;
  /** 网格总列数。 */
  columnCount: number;
  rows?: RowIR[];
  columns?: ColumnIR[];
  /** 稀疏单元格描述，仅写有内容的格子。 */
  cells: CellIR[];
  datasets?: DatasetIR[];
  datasources?: DatasourceIR[];
  paper?: PaperIR;
  /** 报表水印（可选）。 */
  watermark?: WatermarkIR;
  parameters?: ParameterIR[];
  /** 参数查询表单元素（对应 XML <form-element>，主子关联查询表单布局）。 */
  parameterForm?: FormElementIR[];
  /** 填报配置（仅填报类报表有）。 */
  fill?: FillIR;
  /** 报表类型，用于 builder 注册表分发（可选，缺省按 cells 自动推断）。 */
  reportType?:
    | 'detail'
    | 'group'
    | 'crosstab'
    | 'multi-header'
    | 'param-query'
    | 'master-detail';
  /** 预览标题表达式，缺省 fileName()。 */
  title?: string;
}
